import { computed, ref, unref } from 'vue';
import type { MaybeRef } from 'vue';
import type { ApiResponse } from '@/shared/lib';
import type { CaslAction, Subjects } from '@/shared/lib/casl';
import type { MemberPermissionQuery } from '@/entities/Branch/@x/User';
import {
  fetchAllMembersAsUsers,
  resolveMembershipBranchIdForMapping,
} from './branchMembersAsSelectorUsers';
import type { User } from './types';

type PermissionCheck = {
  action: CaslAction;
  subject: Subjects;
};

interface UsePermittedMembersOptions {
  branchId?: MaybeRef<number | undefined>;
  requiredPermissions?: PermissionCheck[];
  matchMode?: 'any' | 'all';
  excludeUserIds?: MaybeRef<number[] | undefined>;
  /**
   * Historical filter screens (approval/verification/homologation/training by elaborator)
   * need to keep offering a collaborator who has since gone inactive, since they're a
   * historical fact of the record. Defaults to `false` because the three "pick someone now"
   * elaborador/aprovador/verificador selects in EditHomologationModal.vue also use this
   * composable (without opting in) to offer only currently-eligible collaborators for a NEW
   * assignment — everything else EditHomologationModal.vue needs (training participants,
   * the branch member lookup used to resolve names/positions) already fetches inactive
   * members through other sources (`usePositionUserSelection`, `fetchAllMembersAsUsers`).
   */
  includeInactiveMembers?: boolean;
}

export function usePermittedMembers({
  branchId,
  requiredPermissions = [],
  matchMode = 'any',
  excludeUserIds,
  includeInactiveMembers = false,
}: UsePermittedMembersOptions = {}) {
  const users = ref<ApiResponse<User[]>>({ success: false, data: [] });
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const buildServerPermissionQuery = (): MemberPermissionQuery | undefined => {
    if (requiredPermissions.length === 0) {
      return undefined;
    }

    if (requiredPermissions.length === 1) {
      const [permission] = requiredPermissions;

      return {
        requiredPermissionAction: permission.action,
        requiredPermissionSubject: String(permission.subject),
      };
    }

    return {
      requiredPermissions: requiredPermissions.map(
        (permission) => `${permission.action}:${String(permission.subject)}`
      ),
      requiredPermissionsMatchMode: matchMode,
    };
  };

  const fetchUsers = async () => {
    const resolvedBranchId = resolveMembershipBranchIdForMapping(unref(branchId));

    if (resolvedBranchId === undefined) {
      users.value = { success: true, data: [] };
      return;
    }

    isLoading.value = true;
    error.value = null;

    try {
      const branchUsers = await fetchAllMembersAsUsers(
        resolvedBranchId,
        buildServerPermissionQuery(),
        includeInactiveMembers ? { includeInactiveMembers: true } : undefined
      );

      const filteredByUser = branchUsers.filter((user) => {
        const excludedIds = unref(excludeUserIds) ?? [];
        return user.id != null && !excludedIds.includes(user.id);
      });

      users.value = { success: true, data: filteredByUser };
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Erro ao buscar membros';
      users.value = { success: false, data: [] };
    } finally {
      isLoading.value = false;
    }
  };

  const userOptions = computed(() =>
    (users.value.data ?? [])
      .map((user) => {
        const id = user.membership?.id ?? user.memberships?.[0]?.id;

        if (id == null) {
          return null;
        }

        return {
          id,
          name: user.name ?? '',
          registrationNumber: user.registrationNumber ?? null,
        };
      })
      .filter(
        (
          option
        ): option is {
          id: number;
          name: string;
          registrationNumber: string | null;
        } => Boolean(option)
      )
  );

  return {
    users,
    userOptions,
    isLoading,
    error,
    fetchUsers,
  };
}
